import { createPortal } from 'react-dom';
import { Alert } from '@/components/primitives';
import { useFeedbackStore } from '@/store/useFeedbackStore';
import { testIds } from '@/test/testIds';
import { zIndex } from '@/theme';
import { opaqueBacking } from './FeedbackBanner.css';

const FLUSH_ALERT_STYLE = { borderRadius: 0, borderLeft: 'none' } as const;

/**
 * Every global banner is opaque (owner principle 23, #1645). An item may still
 * say `push(m, s, { opaque: false })` to stay translucent. Dialog bands are not
 * this banner. See docs/specs/1645-a.md.
 */
export const BANNERS_OPAQUE_BY_DEFAULT = true;

/**
 * The global feedback banner, portalled to `document.body`.
 *
 * The portal is load-bearing, not incidental. FeedbackZone mounts inside
 * AppShell's `<main>`, which sets `position: absolute` + `zIndex: 400` — that
 * combination creates a *stacking context*, so every descendant z-index is
 * resolved inside the 400 layer. Radix portals dialogs to `document.body` at
 * 1300, so before this portal the banner's `zIndex.aboveModal` (1400) could
 * never win: 400 < 1300 meant every banner raised while any dialog was open
 * painted behind the overlay.
 *
 * Portalling to the body escapes *any* ancestor stacking context permanently,
 * which is why this is preferred over hoisting the JSX above `<main>` — that
 * would silently re-break the next time AppShell's layout changes.
 *
 * Note this is the *global* banner. Feedback raised from inside a dialog is
 * routed to that dialog's own inline band instead — see `useFeedbackZone`.
 */
export function FeedbackBanner() {
  const items = useFeedbackStore((s) => s.items);
  const dismiss = useFeedbackStore((s) => s.dismiss);

  if (items.length === 0) return null;

  return createPortal(
    <div
      data-testid={testIds.ui.feedbackZone.banner}
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        zIndex: zIndex.aboveModal,
      }}
    >
      {items.map((item) => {
        const opaque = item.opaque ?? BANNERS_OPAQUE_BY_DEFAULT;
        const alert = (
          <Alert
            key={item.id}
            severity={item.severity}
            onClose={() => dismiss(item.id)}
            data-testid={testIds.ui.feedbackZone.item(item.id)}
            data-opaque={opaque ? 'true' : undefined}
            role="alert"
            style={FLUSH_ALERT_STYLE}
          >
            <span data-testid={testIds.ui.feedbackZone.itemMessage}>
              {item.message}
              {item.count > 1 && ` (×${item.count})`}
            </span>
          </Alert>
        );
        // The tints are translucent; a white backing makes the same tint
        // composite on white so list cards do not show through.
        return opaque ? (
          <div key={item.id} className={opaqueBacking} data-opaque-backing="true">
            {alert}
          </div>
        ) : (
          alert
        );
      })}
    </div>,
    document.body,
  );
}
